import { Calendar, DateField, DatePicker, Input, Label, TextArea } from "@heroui/react";
import { MapPin, Calendar as CalendarIcon, Clock, X } from "lucide-react";
import GooglePlacesSearchDropdown from "@/components/maps/location-search";
import TimeWheelInline from "@/components/v2/time-wheel/time-wheel-inline";
import { getReadableTimeDiff } from "@/utils/dates";

const inputClass =
  "h-10 rounded-xl border border-px border-field-border bg-field-background shadow-none dark:border-white/25";
const labelClass = "flex items-center gap-1.5 text-xs font-medium text-muted dark:text-[#AFAFAF]";

const Field = ({ label, hint, className = "", children }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className={labelClass}>
      {label}
      {hint && <span className="text-xs font-normal text-muted">{hint}</span>}
    </Label>
    {children}
  </div>
);

const DetailsStep = ({ formData, updateFormData }) => {
  return (
    <div className="grid max-w-3xl grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
      <Field
        className="md:col-span-2"
        label={
          <>
            <img src="/icons/title-icon.svg" alt="" className="size-3.5" />
            Title
          </>
        }
      >
        <Input
          placeholder="Quick Sync"
          className={inputClass}
          value={formData.title}
          onChange={(e) => updateFormData({ title: e.target.value })}
        />
      </Field>

      <Field
        label={
          <>
            <CalendarIcon size={13} />
            Event date
          </>
        }
      >
        <DatePicker
          name="date"
          value={formData.date}
          onChange={(dateValue) => updateFormData({ date: dateValue })}
          className="shadow-none"
        >
          <DateField.Group fullWidth className={inputClass}>
            <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
            <DateField.Suffix>
              <DatePicker.Trigger className="border border-separator shadow-none">
                <DatePicker.TriggerIndicator />
              </DatePicker.Trigger>
            </DateField.Suffix>
          </DateField.Group>
          <DatePicker.Popover>
            <Calendar aria-label="Event date">
              <Calendar.Header>
                <Calendar.YearPickerTrigger>
                  <Calendar.YearPickerTriggerHeading />
                  <Calendar.YearPickerTriggerIndicator />
                </Calendar.YearPickerTrigger>
                <Calendar.NavButton slot="previous" />
                <Calendar.NavButton slot="next" />
              </Calendar.Header>
              <Calendar.Grid>
                <Calendar.GridHeader>{(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}</Calendar.GridHeader>
                <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
              </Calendar.Grid>
              <Calendar.YearPickerGrid>
                <Calendar.YearPickerGridBody>
                  {({ year }) => <Calendar.YearPickerCell year={year} />}
                </Calendar.YearPickerGridBody>
              </Calendar.YearPickerGrid>
            </Calendar>
          </DatePicker.Popover>
        </DatePicker>
      </Field>

      <Field
        label={
          <>
            <Clock size={13} />
            Time
          </>
        }
        hint={`· ${getReadableTimeDiff(formData.startTime, formData.endTime)}`}
        className="md:col-span-2"
      >
        <div className="flex items-center gap-2">
          <TimeWheelInline
            value={formData.startTime}
            onSelect={(t) => updateFormData({ startTime: t })}
            minuteStep={15}
            // className="w-32"
            size="sm"
            // columnClassName="h-20"
          />
          <span className="text-muted">–</span>
          <TimeWheelInline size="sm" value={formData.endTime} onSelect={(t) => updateFormData({ endTime: t })} minuteStep={15} />
        </div>
      </Field>

      <Field
        className="md:col-span-2"
        label={
          <>
            <MapPin size={13} />
            Location
          </>
        }
      >
        <GooglePlacesSearchDropdown
          onSelect={(place) => updateFormData({ location: place })}
          placeholder="Type an address or place"
          inputClassName="h-9 border-y-1 border-y-muted/20 border-r-0 bg-background! shadow-none"
          className={inputClass}
        />

        {formData.location && (
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-accent/15 bg-accent/8 px-3 py-2 dark:border-[#FFFFFF26] dark:bg-[#FFFFFF14]">
            <MapPin size={14} className="shrink-0 text-accent dark:text-white" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold">{formData.location.name}</span>
              {formData.location.description && (
                <span className="truncate text-xs text-muted">{formData.location.description}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => updateFormData({ location: null })}
              aria-label="Remove location"
              className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-default hover:text-foreground"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </Field>

      <Field
        className="md:col-span-2"
        label={
          <>
            <img src="/icons/description-icon.svg" alt="" className="size-3.5" />
            Description
          </>
        }
        hint="(Optional)"
      >
        <TextArea
          className={`${inputClass.replace("h-10", "min-h-[90px] py-2")}`}
          value={formData.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
          placeholder="What's this meeting about?"
        />
      </Field>
    <div className="flex flex-col gap-2 text-sm">
        <label className="flex gap-3 items-center">Repeat
          <select aria-label="Repeat frequency" value={formData.recurrence?.frequency || "once"}
            onChange={e => updateFormData({ recurrence: { ...formData.recurrence, frequency: e.target.value, count: formData.recurrence?.count || 4 } })}>
            {["once", "daily", "weekly", "monthly"].map(value => <option key={value}>{value}</option>)}
          </select>
        </label>
        {formData.recurrence?.frequency !== "once" && formData.recurrence && <label>Occurrences (including this meeting)
          <input aria-label="Occurrences" type="number" min="2" max="52" value={formData.recurrence.count}
            onChange={e => updateFormData({ recurrence: { ...formData.recurrence, count: Number(e.target.value) } })} />
        </label>}
      </div>
      </div>
  );
};

export default DetailsStep;